import { defineStore } from 'pinia'
import { AssetStore } from '@/front/stores/asset_store.js'
import { UploadStore } from '@/front/stores/upload_store.js'

const safeGetLocalStorageItem = (key) => {
  try {
    return localStorage.getItem(key)
  } catch (_error) {
    return null
  }
}

const safeSetLocalStorageItem = (key, value) => {
  try {
    localStorage.setItem(key, value)
  } catch (_error) {
    // Ignore storage errors (SecurityError/private mode/quota)
  }
}

const safeRemoveLocalStorageItem = (key) => {
  try {
    localStorage.removeItem(key)
  } catch (_error) {
    // Ignore storage errors
  }
}

const safeGetLocalStorageJSON = (key, fallback) => {
  const raw = safeGetLocalStorageItem(key)
  if (!raw || raw === "undefined") return fallback
  try {
    return JSON.parse(raw)
  } catch (_error) {
    return fallback
  }
}

export const UserStore = defineStore('user', {
  state: () => {
    return {
      user: safeGetLocalStorageJSON("user", {}),
      organization: safeGetLocalStorageJSON("organization", {}),
      orgUsers: safeGetLocalStorageJSON("orgUsers", []),
      activation: safeGetLocalStorageJSON("activation", null),
      billing: {},
      stats: {},
      affiliate: null,
      present: safeGetLocalStorageItem("user") != null
    }
  },

  actions: {

    async fetch(path) {
      return this.Api.get('/users/me').then(response => {  
        this.setUser(response.data)
        log.info(2,"✅ User fetched", response.data.user.email)
      }).catch((err) => {
        this.removeUser()
      })
    },

    async show(id) {
      return this.Api.get(`/views/${id}`).then(response => {  
        
        this.view = response.data.view;        
      })  
    },

    async destroy(id) {      
      return this.Api.destroy(`/views/${id}`).then(response => {  
        this.errors = {};
      }).catch(error => {
        this.errors = error.response.data.errors;
      }) 
    },

    async update(id) {      
      return this.Api.put(`/users/me`, { user: this.user }).then(response => {  
        this.setUser(response.data)
        this.errors = {};
      }).catch(error => {
        this.errors = error.response.data.errors;
      }) 
    },

    async updateOrganization() {      
      return this.Api.put(`/organizations/${this.organization.id}`, this.organization ).then(response => {  
        log.info(2,"Successfully updated organization", response.data)
        this.organization = response.data.organization
        this.errors = {};
      }).catch(error => {
        log.info(2,"Error when updating organization")
        log.info(2,error)
        this.errors = error.response.data.errors;
      }) 
    },
    async addFont(font) {
      if (!this.organization.fonts) {
        this.organization.fonts = []
      }
      this.organization.fonts.push(font)
      this.updateOrganization().then(() => {
        log.info(2,"🌜 Font added", font)
      })
    },

    async fetchOrgUsers() {
      return this.Api.get(`/organizations/users`).then(response => {  
        log.info(2,"🤩🤩🤩 Fetching organization users", response.data)
        this.orgUsers = response.data
        log.info(2,"🤩🤩🤩 Org users", this.orgUsers.active_users)
      })
    },

    async removeUser() {
      log.info(2,"Removing user")
      this.user = {}
      this.organization = {}
      this.orgUsers = {}
      this.activation = null
      this.present = false
      safeRemoveLocalStorageItem("user")
      safeRemoveLocalStorageItem("organization")
      safeRemoveLocalStorageItem("orgUsers")
      safeRemoveLocalStorageItem("activation")
    },

    async deactivateUser(id, transfer_to_user_id) {
      return this.Api.post(`/organizations/users/${id}/deactivate`, { transfer_to_user_id: transfer_to_user_id }).then(response => {
        log.info(2,"User deactivated", response.data)
      })
    },

    setUser(data) {
      if (data.user) {
        this.user = data.user
        this.organization = data.organization
        if (Object.prototype.hasOwnProperty.call(data, 'activation')) {
          this.setActivation(data.activation)
        }
        // this.orgUsers = data.members
        this.user.is_sysadmin = data.is_sysadmin
        this.user.is_brett = data.user.email == "brett@mindstamp.io" || data.user.email == "brett@mindstamp.com"
        safeSetLocalStorageItem("user", JSON.stringify(this.user))
        safeSetLocalStorageItem("organization", JSON.stringify(this.organization))
        safeSetLocalStorageItem("orgUsers", JSON.stringify(this.orgUsers))
        this.present = true
      } else {
        this.present = false
        log.info(2,"❌ No user")
      }
    },

    setActivation(activation) {
      this.activation = activation
      safeSetLocalStorageItem("activation", JSON.stringify(activation))
    },

    async refreshActivation() {
      return this.Api.get(`/activations`).then(response => {
        this.setActivation(response.data.activation)
        return response.data.activation
      })
    },

    async setActivationGoal(goal) {
      return this.Api.post(`/activations/goal`, { goal: goal }).then(response => {
        this.setActivation(response.data.activation)
        return response.data.activation
      })
    },

    async markActivationMilestone(milestone) {
      if (!this.user?.org_owner || this.organization?.plan != 'free') {
        return Promise.resolve(this.activation)
      }
      if (this.activation?.checklist?.[milestone]) {
        return Promise.resolve(this.activation)
      }
      return this.Api.post(`/activations/milestones`, { milestone: milestone }).then(response => {
        this.setActivation(response.data.activation)
        return response.data.activation
      })
    },

    async checkUrlPath(urlPath) {
      return this.Api.get(`/organizations/check_url_path?url_path=${urlPath}`).then(response => {  
        log.info(2,"Valid: ", response.data)
        return response.data.valid
      })  
    },

    async createInvite(email) {
      return this.Api.post(`/invites`, { email: email }).then(response => {  
        log.info(2,"Invite created", response.data)
        return response.data
      })
    },

    async destroyInvite(id) {
      return this.Api.destroy(`/invites/${id}`).then(response => {  
        log.info(2,"Invite destroyed", response.data)
      })
    },

    async remindInvite(id) {
      return this.Api.post(`/invites/${id}/remind`).then(response => {  
        log.info(2,"Invite reminded", response.data)
      })
    },

    async fetchOrgQuotas() {
      return this.Api.get(`/organizations/quotas`).then(response => {  
        log.info(2,"Quotas fetched", response.data)
        this.orgQuotas = response.data
      })
    },

    async fetchStats() {
      return this.Api.get(`/users/stats`).then(response => {  
        log.info(2,"Stats fetched", response.data)
        this.stats = response.data
      })
    },

    // Billing related methods
    // Move to own store?
    async startCheckout(plan, annual, rewardful_id) {
      return this.Api.get(`/billing/checkout?plan=${plan}&annual=${annual}&rewardful_id=${rewardful_id}`).then(response => {
        log.info(2,"Checkout", response.data)
        return response.data
      })
    },

    async verifyStripeIntegrity() {
      return this.Api.get(`/organizations/verify_stripe_integrity`).then(response => {  
        log.info(2,"Stripe integrity verified", response.data)
      })
    },

    async fetchBilling() {
      return this.fetch().then(() => {  
        return this.Api.get(`/billing`).then(response => {  
          log.info(2,"Billing fetched", response.data)
          this.billing = response.data
        })
      })
    },

    async getCurrentInvoice() {
      return this.Api.get(`/billing/current_invoice`).then(response => {  
        log.info(2,"Current invoice fetched", response.data)
        return response.data
      })
    },

    async keepSubscription() {
      return this.Api.post(`/billing/keep_subscription`).then(response => {  
        log.info(2,"Subscription kept", response.data)
      })
    },

    async cancelSubscription(reason, answer) {
      return this.Api.post(`/billing/cancel_subscription`, { reason: reason, answer: answer }).then(response => {  
        log.info(2,"Subscription canceled", response.data)
      })
    },

    async pauseSubscription() {
      return this.Api.post(`/billing/pause_subscription`).then(response => {  
        log.info(2,"Subscription paused", response.data)
      })
    },

    async resumeSubscription() {
      return this.Api.post(`/billing/resume_subscription`).then(response => {  
        log.info(2,"Subscription resumed", response.data)
      })
    },

    async startPortal() {
      return this.Api.get(`/billing/portal`).then(response => {  
        log.info(2,"Stripe portal session fetched", response.data)
        return response.data
      })
    },

    async doAffiliateSignup() {
      return this.Api.post(`/users/affiliates`).then(response => {  
        log.info(2,"Affiliate signup", response.data)
        return response
      })
    },

    async getAffiliate() {
      return this.Api.get(`/users/affiliates/me`).then(response => {  
        log.info(2,"Affiliate fetched", response.data)
        this.affiliate = response.data.affiliate
      })
    },
  },
  getters: {
    canUseDriveIntegration: (state) => {
      return state.user.email.includes('@mindstamp.com') || state.user.email.includes('@mindstamp.io')
    },
    deactivated: (state) => {
      return state.user && state.user.id && !state.user.subscribed && state.organization.active
    },
    expired: (state) => {
      return state.user && state.user.id && !state.organization.active
    },
    outOfSpace: (state) => {
      const assetStore = AssetStore()
      const uploadStore = UploadStore()
      return !uploadStore.videoToReplace && (state.organization.video_quota - assetStore.videos.length <= 0)
    },
    plan: (state) => {
      return {
        free: state.organization.plan == 'free',
        starter: state.organization.plan == 'starter',
        core: state.organization.plan == 'core',
        pro: state.organization.plan == 'pro',
        enterprise: state.organization.plan == 'enterprise',
        starterPlus: state.organization.plan == 'starter' || state.organization.plan == 'core' || state.organization.plan == 'pro' || state.organization.plan == 'enterprise',
        corePlus: state.organization.plan == 'core' || state.organization.plan == 'pro' || state.organization.plan == 'enterprise', 
        proPlus: state.organization.plan == 'pro' || state.organization.plan == 'enterprise',
      }
    },
    canAccess: (state) => {
      return {
        plan: state.organization.plan,
        new_video_upload: state.organization.can_upload,

        // Core Plus
        precise: state.plan.free || state.plan.corePlus || state.organization.can_use_precise,
        resume: state.plan.free || state.plan.corePlus || state.organization.can_resume,
        location: state.plan.free || state.plan.corePlus || state.organization.can_location,
        verify: state.plan.free || state.plan.corePlus || state.organization.can_verify,
        prevent: state.plan.free || state.plan.corePlus || state.organization.can_prevent,
        clickfunnels: state.plan.free || state.plan.proPlus || state.organization.clickfunnels_access,
        scorm: state.plan.free || state.plan.corePlus || state.organization.can_scorm,
        variables: state.plan.free || state.plan.corePlus || state.organization.variables_access,
        groups: state.plan.corePlus || state.organization.groups_access,
        series: state.plan.free || state.plan.corePlus || state.organization.series_access,
        unbranded: state.plan.corePlus,
        payper: state.plan.corePlus,
        google_analytics: state.plan.corePlus,
        facebook: state.plan.corePlus,
        zapier: state.plan.free || state.plan.corePlus,
        view_webhooks: state.plan.corePlus,
        internal_label: state.plan.corePlus,
        url_path: state.plan.corePlus,
        transcriptions: state.plan.free || state.plan.corePlus,
        tab_title: state.plan.free || state.plan.corePlus,
        fonts: state.plan.corePlus,
        transitions: state.plan.free || state.plan.corePlus,
        animations: state.plan.free || state.plan.corePlus,
        play_button: state.plan.free || state.plan.corePlus,
        domain_whitelist: state.plan.corePlus,
        
        // Pro plus
        interaction_webhooks: state.plan.proPlus,
        url_title: state.plan.proPlus,
        sticky: state.plan.free || state.plan.proPlus,
        files: state.plan.free || state.plan.proPlus,
        lead_webhooks: state.plan.proPlus,
        custom_domain: state.plan.proPlus,
        hubspot: state.plan.free || state.plan.proPlus,
        recurring_exports: state.plan.proPlus,
        api: state.plan.proPlus || state.organization.api_access,
        export: state.plan.proPlus || state.organization.export_access,
        branding: state.plan.free || state.plan.proPlus || state.organization.branding_access,
        file_upload: state.plan.proPlus || state.organization.can_file_upload,
        conditional_logic: state.plan.free || state.plan.proPlus || state.organization.conditional_access,
        lists: state.plan.free || state.plan.proPlus || state.organization.lists_access,

      }
    }
  }
})
